import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";

export default function Hero() {
  const { config, isPreview } = useSite();
  const showTalentsCta = isPreview || config?.nav_show_talents !== false;
  const showContactCta = isPreview || config?.nav_show_contact !== false;
  const words = config?.hero_rotating_words?.length
    ? config.hero_rotating_words
    : ["Influencers", "Streaming", "Contenido", "Eventos", "Productos", "Bolivia"];

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % words.length), 2200);
    return () => clearInterval(t);
  }, [words.length]);

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden grid-bg" data-testid="hero">
      {/* Background: image or video override */}
      {config?.hero_background_type === "image" && config?.hero_background_image && (
        <>
          <img src={config.hero_background_image.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${config.hero_background_image}` : config.hero_background_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/70" />
        </>
      )}
      {config?.hero_background_type === "video" && config?.hero_background_video && (
        <>
          <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src={config.hero_background_video.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${config.hero_background_video}` : config.hero_background_video} />
          </video>
          <div className="absolute inset-0 bg-background/70" />
        </>
      )}
      {/* Glow orbs */}
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-primary/30 blur-[120px] animate-pulse-slow" />
      <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-accent/30 blur-[120px] animate-pulse-slow" />
      <div className="absolute inset-0 noise" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-background/60 backdrop-blur-sm mb-8"
        >
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Agencia Full-Service · Bolivia</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-black text-6xl sm:text-7xl lg:text-9xl tracking-tighter leading-[0.9] mb-6"
          data-testid="hero-title"
        >
          {config?.hero_title || "INFLUCAST"}
        </motion.h1>

        <div className="flex flex-wrap items-baseline gap-x-3 mb-8 max-w-4xl">
          <span className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight">Conectamos marcas con</span>
          <AnimatePresence mode="wait">
            <motion.span
              key={words[idx]}
              initial={{ y: 30, opacity: 0, filter: "blur(8px)" }}
              animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
              exit={{ y: -30, opacity: 0, filter: "blur(8px)" }}
              transition={{ duration: 0.45 }}
              className="font-display text-2xl sm:text-3xl lg:text-5xl font-black tracking-tight gradient-text"
              data-testid="hero-rotating-word"
            >
              {words[idx]}
            </motion.span>
          </AnimatePresence>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-10 text-balance"
        >
          {config?.hero_description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Link
            to="/talentos"
            data-testid="hero-cta-primary"
            className={`${showTalentsCta ? "group inline-flex" : "hidden"} items-center justify-center gap-2 px-7 py-3.5 bg-primary text-primary-foreground font-semibold rounded-sm hover:opacity-90 transition-all`}
          >
            {config?.hero_cta_primary || "Conoce nuestros talentos"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/contacto"
            data-testid="hero-cta-secondary"
            className={`${showContactCta ? "inline-flex" : "hidden"} items-center justify-center gap-2 px-7 py-3.5 border border-border bg-background/60 backdrop-blur-sm font-semibold rounded-sm hover:border-primary hover:text-primary transition-colors`}
          >
            {config?.hero_cta_secondary || "Quiero trabajar con INFLUCAST"}
          </Link>
        </motion.div>

        {/* scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.3em] text-muted-foreground hidden lg:flex items-center gap-2">
          <div className="w-12 h-px bg-border" />
          Scroll
          <div className="w-12 h-px bg-border" />
        </div>
      </div>
    </section>
  );
}
